import { FC, Fragment, useEffect, useMemo, useState } from 'react';

import {
  BasicProviderUserDataFragment,
  Feature,
  PatientContact,
  PatientDataPermission,
  ProgramNoteSharesForContactDocument,
  ProviderUserMembersV2Query,
  useContactsForProgramQuery,
  useCreateProgramNoteShareMutation,
  useNotifyProgramNoteMutation,
  useProviderUserMembersV2Query,
} from '../../../../generated/graphql';
import { Combobox, Transition } from '@headlessui/react';
import { ChevronDownIcon, TrashIcon } from '@heroicons/react/outline';

import Avatar from '../../Avatar';
import ModalDialog from '../../ModalDialog';
import { customToast } from '../../ToastAlert/customToast';
import Spinner from '../../../svgs/Spinner';
import { formatContactTypeLabel } from '../../../lib/patientContactLabels';
import { useFeatureFlags } from '../../../../contexts/FeatureFlagContext';

type NotifyProviderUsersModalProps = {
  isOpen: boolean;
  setClosed: () => void;
  programNoteId: string;
  programId: string;
  noteTitle?: string | null;
  authedProviderUserId: string;
  onSuccess: (params: {
    notifiedUsers: BasicProviderUserDataFragment[];
    sharedContacts: Array<{ name: string; email: string }>;
  }) => void;
};

type ProviderUserOption = NonNullable<
  ProviderUserMembersV2Query['providerUserMembers'][number]
>;

type ContactOption = PatientContact;
type ShareTarget = 'teammates' | 'contacts';

function getContactDisplayName(contact: ContactOption): string {
  const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ');
  return name || contact.email?.trim() || 'Contact';
}

const NotifyProviderUsersModal: FC<NotifyProviderUsersModalProps> = ({
  isOpen,
  setClosed,
  programNoteId,
  programId,
  noteTitle,
  authedProviderUserId,
  onSuccess,
}) => {
  const [shareTarget, setShareTarget] = useState<ShareTarget>('teammates');
  const [providerUserQuery, setProviderUserQuery] = useState('');
  const [contactQuery, setContactQuery] = useState('');
  const [selectedProviderUsers, setSelectedProviderUsers] = useState<
    ProviderUserOption[]
  >([]);
  const [selectedContacts, setSelectedContacts] = useState<ContactOption[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isFeatureEnabled } = useFeatureFlags();
  const isExternalNoteShareEnabled = isFeatureEnabled(
    Feature.ExternalNoteShare,
  );

  const {
    data: providerUsersData,
    loading: providerUsersLoading,
    error: providerUsersError,
  } = useProviderUserMembersV2Query({
    variables: {
      activeProviderUsersOnly: true,
    },
    fetchPolicy: 'cache-first',
    skip: !isOpen,
  });

  const {
    data: contactsData,
    loading: contactsLoading,
    error: contactsError,
  } = useContactsForProgramQuery({
    variables: { programId },
    skip: !isOpen || !programId || !isExternalNoteShareEnabled,
    fetchPolicy: isOpen ? 'network-only' : 'cache-first',
  });

  const [notifyProgramNote, { loading: notifyLoading }] =
    useNotifyProgramNoteMutation();
  const [createProgramNoteShare, { loading: shareLoading }] =
    useCreateProgramNoteShareMutation();

  useEffect(() => {
    if (providerUsersError) {
      customToast.error(
        'Something went wrong getting your team members. Please try again.',
      );
    }
  }, [providerUsersError]);

  useEffect(() => {
    if (contactsError) {
      customToast.error(
        'Something went wrong getting contacts. Please try again.',
      );
    }
  }, [contactsError]);

  useEffect(() => {
    if (!isOpen) {
      setShareTarget('teammates');
      setProviderUserQuery('');
      setContactQuery('');
      setSelectedProviderUsers([]);
      setSelectedContacts([]);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const eligibleProviderUsers = useMemo<ProviderUserOption[]>(() => {
    return (
      providerUsersData?.providerUserMembers?.filter(
        (user): user is ProviderUserOption =>
          Boolean(user?.id) &&
          user?.id !== authedProviderUserId &&
          (user?.patientDataPermission !== PatientDataPermission.None ||
            user?.isExternalCollaborator === true),
      ) ?? []
    );
  }, [providerUsersData?.providerUserMembers, authedProviderUserId]);

  const eligibleContacts = useMemo<ContactOption[]>(() => {
    return (contactsData?.contactsForProgram ?? []).filter(
      (contact): contact is ContactOption =>
        Boolean(contact?.id) && Boolean(contact?.email?.trim()),
    );
  }, [contactsData?.contactsForProgram]);

  const showContactsOption =
    isExternalNoteShareEnabled &&
    !contactsLoading &&
    eligibleContacts.length > 0;

  useEffect(() => {
    // Contacts may finish loading empty — fall back to teammates-only.
    if (!showContactsOption && shareTarget === 'contacts') {
      setShareTarget('teammates');
      setSelectedContacts([]);
      setContactQuery('');
    }
  }, [showContactsOption, shareTarget]);

  const selectShareTarget = (target: ShareTarget) => {
    if (target === shareTarget) return;
    setShareTarget(target);
    if (target === 'teammates') {
      setSelectedContacts([]);
      setContactQuery('');
    } else {
      setSelectedProviderUsers([]);
      setProviderUserQuery('');
    }
  };

  const selectedProviderUserIds = selectedProviderUsers.map((user) => user.id);
  const selectedContactIds = selectedContacts.map((contact) => contact.id);

  const availableProviderUsers = useMemo(() => {
    return eligibleProviderUsers.filter(
      (providerUser) => !selectedProviderUserIds.includes(providerUser.id),
    );
  }, [eligibleProviderUsers, selectedProviderUserIds]);

  const availableContacts = useMemo(() => {
    return eligibleContacts.filter(
      (contact) => !selectedContactIds.includes(contact.id),
    );
  }, [eligibleContacts, selectedContactIds]);

  const filteredProviderUsers = useMemo(() => {
    if (!providerUserQuery) return availableProviderUsers;

    const normalizedQuery = providerUserQuery.toLowerCase();
    return availableProviderUsers.filter((user) => {
      return (
        user.name?.toLowerCase().includes(normalizedQuery) ||
        user.email?.toLowerCase().includes(normalizedQuery)
      );
    });
  }, [availableProviderUsers, providerUserQuery]);

  const filteredContacts = useMemo(() => {
    if (!contactQuery) return availableContacts;

    const normalizedQuery = contactQuery.toLowerCase();
    return availableContacts.filter((contact) => {
      const displayName = getContactDisplayName(contact).toLowerCase();
      return (
        displayName.includes(normalizedQuery) ||
        contact.email?.toLowerCase().includes(normalizedQuery) ||
        (contact.contactType
          ? formatContactTypeLabel(contact.contactType)
              .toLowerCase()
              .includes(normalizedQuery)
          : false)
      );
    });
  }, [availableContacts, contactQuery]);

  const removeProviderUser = (providerUserId: string) => {
    setSelectedProviderUsers((prev) =>
      prev.filter((providerUser) => providerUser.id !== providerUserId),
    );
  };

  const removeContact = (contactId: string) => {
    setSelectedContacts((prev) =>
      prev.filter((contact) => contact.id !== contactId),
    );
  };

  const handleShare = async () => {
    const sharingWithTeammates = shareTarget === 'teammates';
    if (sharingWithTeammates && !selectedProviderUsers.length) {
      customToast.error('Select at least one teammate to continue.');
      return;
    }
    if (!sharingWithTeammates && !selectedContacts.length) {
      customToast.error('Select at least one contact to continue.');
      return;
    }

    setIsSubmitting(true);

    let notifiedUsers: BasicProviderUserDataFragment[] = [];
    const omittedTeammateNames: string[] = [];
    const sharedContacts: Array<{ id: string; name: string; email: string }> =
      [];
    const failedContactNames: string[] = [];
    let teammatesFailed = false;

    if (sharingWithTeammates) {
      try {
        const response = await notifyProgramNote({
          variables: {
            input: {
              programNoteId,
              providerUserIds: selectedProviderUsers.map((user) => user.id),
            },
          },
        });

        const result = response.data?.notifyProgramNote;
        if (!result) {
          throw new Error('No response received.');
        }

        notifiedUsers =
          result.notifiedProviderUsers?.filter(
            (user): user is BasicProviderUserDataFragment => Boolean(user?.id),
          ) ?? [];
        const notifiedIds = new Set(notifiedUsers.map((user) => user.id));
        // Server may omit inactive/ineligible teammates without erroring.
        for (const user of selectedProviderUsers) {
          if (!notifiedIds.has(user.id)) {
            omittedTeammateNames.push(user.name?.trim() || 'Teammate');
          }
        }
      } catch {
        teammatesFailed = true;
      }
    } else {
      for (const contact of selectedContacts) {
        if (!contact.id) continue;
        try {
          const response = await createProgramNoteShare({
            variables: {
              input: {
                programNoteId,
                recipientContactId: contact.id,
              },
            },
            refetchQueries: [
              {
                query: ProgramNoteSharesForContactDocument,
                variables: { contactId: contact.id },
              },
            ],
          });
          const result = response.data?.createProgramNoteShare;
          if (!result) {
            throw new Error('Failed to share with a contact.');
          }
          sharedContacts.push({
            id: contact.id,
            name:
              result.recipientName?.trim() || getContactDisplayName(contact),
            email: result.recipientEmail,
          });
        } catch {
          failedContactNames.push(getContactDisplayName(contact));
        }
      }
    }

    const teammatesSucceeded = notifiedUsers.length > 0;
    const anySuccess = teammatesSucceeded || sharedContacts.length > 0;
    const anyFailure =
      teammatesFailed ||
      omittedTeammateNames.length > 0 ||
      failedContactNames.length > 0;

    try {
      if (anySuccess) {
        onSuccess({
          notifiedUsers,
          sharedContacts: sharedContacts.map(({ name, email }) => ({
            name,
            email,
          })),
        });
      }

      if (!anyFailure && anySuccess) {
        setSelectedProviderUsers([]);
        setSelectedContacts([]);
        setClosed();
        return;
      }

      if (anySuccess && anyFailure) {
        // Drop only recipients that already succeeded so retry is safe.
        const notifiedIds = new Set(notifiedUsers.map((user) => user.id));
        setSelectedProviderUsers((prev) =>
          prev.filter((user) => !notifiedIds.has(user.id)),
        );
        const succeededContactIds = new Set(
          sharedContacts.map((contact) => contact.id),
        );
        setSelectedContacts((prev) =>
          prev.filter(
            (contact) => !contact.id || !succeededContactIds.has(contact.id),
          ),
        );

        const failureParts: string[] = [];
        if (teammatesFailed) {
          failureParts.push('teammates');
        } else if (omittedTeammateNames.length) {
          failureParts.push(omittedTeammateNames.join(', '));
        }
        if (failedContactNames.length) {
          failureParts.push(failedContactNames.join(', '));
        }
        customToast.error(
          `Couldn't finish sharing with ${failureParts.join(
            '; ',
          )}. Successful recipients were updated — retry the rest.`,
        );
        return;
      }

      customToast.error(
        sharingWithTeammates
          ? 'Unable to notify teammates. Please try again.'
          : 'Unable to share note. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasRecipients =
    shareTarget === 'teammates'
      ? selectedProviderUsers.length > 0
      : selectedContacts.length > 0;
  const isShareInFlight = isSubmitting || notifyLoading || shareLoading;
  const isLoading =
    isShareInFlight ||
    providerUsersLoading ||
    (shareTarget === 'contacts' && contactsLoading);
  const isSubmitDisabled = !hasRecipients || isLoading;
  const primaryActionTitle =
    shareTarget === 'teammates' ? 'Notify teammates' : 'Share with contacts';

  const handleClose = () => {
    // Keep modal open while share/notify is in flight so partial-success
    // selection state is not discarded by Escape / backdrop / X.
    if (isShareInFlight) return;
    setClosed();
  };

  return (
    <ModalDialog
      title="Share note"
      isOpen={isOpen}
      setClosed={handleClose}
      isBlocking={isShareInFlight}
      fetching={isLoading}
      width="medium"
      className="flex min-h-[70vh] flex-col"
      bodyClassName="flex-1 space-y-5 overflow-visible"
      primaryActionTitle={primaryActionTitle}
      primaryActionOnClick={handleShare}
      primaryActionDisabled={isSubmitDisabled}
    >
      <div className="flex flex-col space-y-2">
        <p className="text-body text-neutral-125">
          {shareTarget === 'teammates'
            ? `Notify teammates about “${noteTitle || 'Untitled note'}”.`
            : `Share a secure link to “${
                noteTitle || 'Untitled note'
              }” with contacts.`}
        </p>
      </div>

      {providerUsersLoading && contactsLoading ? (
        <div className="flex justify-center py-6">
          <Spinner className="h-5 w-5 text-neutral-125" />
        </div>
      ) : (
        <div className="flex flex-1 flex-col space-y-5 p-5 sm:mx-auto sm:max-w-[520px]">
          {showContactsOption && (
            <div
              className="flex space-x-1 rounded-xl bg-neutral-50 p-1"
              role="tablist"
              aria-label="Share destination"
            >
              <button
                type="button"
                role="tab"
                aria-selected={shareTarget === 'teammates'}
                onClick={() => selectShareTarget('teammates')}
                className={`flex-1 rounded-lg px-3 py-2 text-caption font-medium transition-colors ${
                  shareTarget === 'teammates'
                    ? 'bg-white text-green-150 shadow-sm'
                    : 'text-neutral-125 hover:text-neutral-150'
                }`}
              >
                Teammates
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={shareTarget === 'contacts'}
                onClick={() => selectShareTarget('contacts')}
                className={`flex-1 rounded-lg px-3 py-2 text-caption font-medium transition-colors ${
                  shareTarget === 'contacts'
                    ? 'bg-white text-green-150 shadow-sm'
                    : 'text-neutral-125 hover:text-neutral-150'
                }`}
              >
                Contacts
              </button>
            </div>
          )}

          {shareTarget === 'teammates' ? (
            <section className="flex flex-col space-y-3">
              <div>
                {!showContactsOption && (
                  <h3 className="text-caption font-medium text-neutral-150">
                    Teammates
                  </h3>
                )}
                <p className="text-small-caption text-neutral-110">
                  Email a link to view in Homecoming
                </p>
              </div>

              {providerUsersLoading ? (
                <div className="flex justify-center py-4">
                  <Spinner className="h-5 w-5 text-neutral-125" />
                </div>
              ) : (
                <>
                  <Combobox
                    as="div"
                    className="relative w-full text-body"
                    value={selectedProviderUsers}
                    onChange={(users) => {
                      setProviderUserQuery('');
                      setSelectedProviderUsers(users);
                    }}
                    multiple
                  >
                    <div className="relative">
                      <Combobox.Input
                        className="relative flex w-full cursor-text flex-row items-center py-3 pl-4 pr-9 text-left text-body text-green-150 placeholder:text-neutral-125/75 focus:outline-none focus:ring-2 focus:ring-secondary-50"
                        onChange={(event) =>
                          setProviderUserQuery(event.target.value)
                        }
                        placeholder="Search teammates"
                        value={providerUserQuery}
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-3">
                        <ChevronDownIcon
                          className="h-5 w-5 text-neutral-125"
                          aria-hidden="true"
                        />
                      </Combobox.Button>
                    </div>
                    <Transition
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                      afterLeave={() => setProviderUserQuery('')}
                    >
                      <Combobox.Options className="no-scrollbar absolute left-0 right-0 z-10 mt-2 max-h-[240px] w-full divide-y divide-neutral-75 overflow-auto rounded-lg border border-neutral-75 bg-white py-1 text-caption shadow-lg focus:outline-none focus:ring-0">
                        {filteredProviderUsers.length === 0 ? (
                          <div className="py-3 px-5 text-center text-neutral-110">
                            No teammates found.
                          </div>
                        ) : (
                          filteredProviderUsers.map((providerUser) => {
                            const displayName =
                              providerUser.name?.trim() ||
                              providerUser.email?.trim() ||
                              'Teammate';
                            return (
                              <Combobox.Option
                                key={providerUser.id}
                                value={providerUser}
                              >
                                {({ active, selected }) => (
                                  <div
                                    className={`relative flex cursor-pointer select-none flex-row items-center truncate py-3 px-5 text-green-150 hover:bg-neutral-25 ${
                                      active || selected ? 'bg-neutral-25' : ''
                                    }`}
                                  >
                                    <Avatar
                                      size="small"
                                      name={displayName}
                                      imageUrl={
                                        providerUser.profileImageMedia?.url ??
                                        undefined
                                      }
                                      className="mr-3"
                                    />
                                    <div className="flex flex-col">
                                      <span>{displayName}</span>
                                      <span className="text-small-caption text-neutral-110">
                                        {providerUser.email}
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </Combobox.Option>
                            );
                          })
                        )}
                      </Combobox.Options>
                    </Transition>
                  </Combobox>

                  {Boolean(selectedProviderUsers.length) ? (
                    <ul className="no-scrollbar flex max-h-40 w-full flex-col gap-3 overflow-y-auto">
                      {selectedProviderUsers.map((providerUser) => {
                        const displayName =
                          providerUser.name?.trim() ||
                          providerUser.email?.trim() ||
                          'Teammate';
                        return (
                          <li
                            key={providerUser.id}
                            className="flex w-full flex-row items-center justify-between rounded-lg border border-neutral-75 bg-white py-2 px-3"
                          >
                            <div className="flex flex-row items-center">
                              <Avatar
                                size="medium"
                                name={displayName}
                                imageUrl={
                                  providerUser.profileImageMedia?.url ??
                                  undefined
                                }
                              />
                              <div className="ml-3 flex flex-col">
                                <span className="font-serif text-category">
                                  {displayName}
                                </span>
                                <span className="text-small-caption text-neutral-110">
                                  {providerUser.email}
                                </span>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                removeProviderUser(providerUser.id)
                              }
                              className="rounded-full p-1 text-neutral-125 hover:bg-neutral-125 hover:text-white focus:outline-none"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <div className="rounded-md border border-dashed border-neutral-75 py-4 text-center text-small-caption text-neutral-110">
                      Selected teammates will appear here.
                    </div>
                  )}
                </>
              )}
            </section>
          ) : (
            <section className="flex flex-col space-y-3">
              <div>
                <p className="text-small-caption text-neutral-110">
                  Email a secure link to view and download (no Homecoming login)
                </p>
              </div>

              {contactsLoading ? (
                <div className="flex justify-center py-4">
                  <Spinner className="h-5 w-5 text-neutral-125" />
                </div>
              ) : (
                <>
                  <Combobox
                    as="div"
                    className="relative w-full text-body"
                    value={selectedContacts}
                    onChange={(contacts) => {
                      setContactQuery('');
                      setSelectedContacts(contacts);
                    }}
                    multiple
                  >
                    <div className="relative">
                      <Combobox.Input
                        className="relative flex w-full cursor-text flex-row items-center py-3 pl-4 pr-9 text-left text-body text-green-150 placeholder:text-neutral-125/75 focus:outline-none focus:ring-2 focus:ring-secondary-50"
                        onChange={(event) =>
                          setContactQuery(event.target.value)
                        }
                        placeholder="Search contacts"
                        value={contactQuery}
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-3">
                        <ChevronDownIcon
                          className="h-5 w-5 text-neutral-125"
                          aria-hidden="true"
                        />
                      </Combobox.Button>
                    </div>
                    <Transition
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                      afterLeave={() => setContactQuery('')}
                    >
                      <Combobox.Options className="no-scrollbar absolute left-0 right-0 z-10 mt-2 max-h-[240px] w-full divide-y divide-neutral-75 overflow-auto rounded-lg border border-neutral-75 bg-white py-1 text-caption shadow-lg focus:outline-none focus:ring-0">
                        {filteredContacts.length === 0 ? (
                          <div className="py-3 px-5 text-center text-neutral-110">
                            No contacts found.
                          </div>
                        ) : (
                          filteredContacts.map((contact) => {
                            const displayName = getContactDisplayName(contact);
                            return (
                              <Combobox.Option key={contact.id} value={contact}>
                                {({ active, selected }) => (
                                  <div
                                    className={`relative flex cursor-pointer select-none flex-row items-center truncate py-3 px-5 text-green-150 hover:bg-neutral-25 ${
                                      active || selected ? 'bg-neutral-25' : ''
                                    }`}
                                  >
                                    <Avatar
                                      size="small"
                                      name={displayName}
                                      className="mr-3"
                                    />
                                    <div className="flex flex-col">
                                      <span>{displayName}</span>
                                      <span className="text-small-caption text-neutral-110">
                                        {contact.email}
                                        {contact.contactType
                                          ? ` · ${formatContactTypeLabel(
                                              contact.contactType,
                                            )}`
                                          : ''}
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </Combobox.Option>
                            );
                          })
                        )}
                      </Combobox.Options>
                    </Transition>
                  </Combobox>

                  {Boolean(selectedContacts.length) ? (
                    <ul className="no-scrollbar flex max-h-40 w-full flex-col gap-3 overflow-y-auto">
                      {selectedContacts.map((contact) => {
                        const displayName = getContactDisplayName(contact);
                        return (
                          <li
                            key={contact.id}
                            className="flex w-full flex-row items-center justify-between rounded-lg border border-neutral-75 bg-white py-2 px-3"
                          >
                            <div className="flex flex-row items-center">
                              <Avatar size="medium" name={displayName} />
                              <div className="ml-3 flex flex-col">
                                <span className="font-serif text-category">
                                  {displayName}
                                </span>
                                <span className="text-small-caption text-neutral-110">
                                  {contact.email}
                                  {contact.contactType
                                    ? ` · ${formatContactTypeLabel(
                                        contact.contactType,
                                      )}`
                                    : ''}
                                </span>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                contact.id && removeContact(contact.id)
                              }
                              className="rounded-full p-1 text-neutral-125 hover:bg-neutral-125 hover:text-white focus:outline-none"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <div className="rounded-md border border-dashed border-neutral-75 py-4 text-center text-small-caption text-neutral-110">
                      Selected contacts will appear here.
                    </div>
                  )}
                </>
              )}
            </section>
          )}
        </div>
      )}
    </ModalDialog>
  );
};

export default NotifyProviderUsersModal;
